import { useTranslation } from 'react-i18next';
import NadaConsta from '../../../../components/nadaConsta';
import {
  Badge,
  Campos,
  Container,
  Mensagem,
  Resumos,
  Score,
  Secao,
  Tabela,
  formatarCentavos,
  formatarData,
  formatarDocumento,
  formatarTexto,
  rotulo,
} from './ui';

const NIVEL_RISCO: Record<string, string> = {
  low: 'Risco baixo',
  medium: 'Risco médio',
  high: 'Risco alto',
  very_high: 'Risco muito alto',
};

const CATEGORIAS: Record<string, string> = {
  pefin: 'Pefin',
  refin: 'Refin',
  protestos: 'Protestos',
  acoes_judiciais: 'Ações judiciais',
  bounced_check: 'Cheques sem fundo',
  overdue: 'Dívidas vencidas',
  bankruptcy: 'Falências',
};

const colunasRegistro = [
  { chave: 'credor', titulo: 'Credor' },
  { chave: 'natureza_operacao', titulo: 'Natureza' },
  { chave: 'contrato', titulo: 'Contrato', render: (r: any) => r?.campos_adicionais?.contract_number || '—' },
  { chave: 'data', titulo: 'Data' },
  { chave: 'situacao', titulo: 'Situação', render: (r: any) => (r?.situacao === 'active' ? 'Ativa' : formatarTexto(r?.situacao)) },
  { chave: 'valor_cents', titulo: 'Valor', destaque: true },
];

export function SerasaPremium({ data, isConsultaNova }: { data: any; isConsultaNova?: boolean }) {
  const { t } = useTranslation();

  const org = data?.organizado ?? {};
  const consulta = org.consulta ?? {};
  const titular = org.titular ?? {};
  const endereco = titular.endereco ?? {};
  const score = org.score ?? {};
  const resumo = org.resumo ?? {};
  const isPJ = consulta.tipo_pessoa === 'PJ' || String(titular.documento ?? '').length === 14;

  // "outras" agrupa categorias extras (cheque sem fundo, dívida vencida...) num nível abaixo
  const { outras, ...principais } = org.restricoes ?? {};
  const categorias = Object.entries({ ...principais, ...(outras ?? {}) }) as [string, any][];
  const comRegistro = categorias.filter(([, c]) => c?.quantidade > 0 || c?.registros?.length);

  const consultas = org.consultas_ao_documento;
  const socios: any[] = org.socios ?? [];
  const administradores: any[] = org.administradores ?? [];
  const temRestricao = !!resumo.tem_restricao;

  return (
    <Container
      titulo={t('creditoCompletoScore')}
      isConsultaNova={isConsultaNova}
      alerta={temRestricao}
      badge={<Badge texto={temRestricao ? 'Possui restrições' : 'Sem restrições'} tom={temRestricao ? 'vermelho' : 'verde'} />}
    >
      <Campos
        campos={[
          { label: isPJ ? 'CNPJ' : 'CPF', value: formatarDocumento(titular.documento ?? consulta.documento) },
          { label: isPJ ? 'Razão social' : 'Nome', value: titular.nome ?? consulta.nome },
          { label: 'Situação cadastral', value: consulta.situacao_cadastral },
          { label: 'Data de nascimento', value: titular.nascimento && formatarData(titular.nascimento) },
          { label: 'Nome da mãe', value: titular.nome_mae },
          { label: 'Fundação', value: titular.fundacao && formatarData(titular.fundacao) },
          { label: 'Idade da empresa', value: titular.idade_empresa },
          {
            label: 'Endereço',
            value: [endereco.logradouro, endereco.bairro, [endereco.cidade, endereco.uf].filter(Boolean).join('/'), endereco.cep]
              .filter(Boolean)
              .join(' - '),
          },
          { label: 'Protocolo', value: consulta.protocolo },
          { label: 'Data da consulta', value: consulta.data_hora && formatarData(consulta.data_hora) },
        ]}
      />

      <Secao titulo="Score">
        <Score
          valor={score.valor}
          minimo={score.minimo}
          maximo={score.maximo}
          faixa={NIVEL_RISCO[score.nivel_risco] ?? score.nivel_risco}
          detalhes={[
            { label: 'Escala', value: score.escala },
            { label: 'Probabilidade de inadimplência', value: score.probabilidade_inadimplencia },
          ]}
        />
      </Secao>

      <Secao titulo="Restrições">
        {!temRestricao && !comRegistro.length ? (
          <NadaConsta />
        ) : (
          <>
            <Resumos
              itens={[
                { label: 'Ocorrências', value: resumo.total_ocorrencias ?? 0, icon: 'pi pi-file' },
                { label: 'Valor total', value: formatarCentavos(resumo.valor_total_cents), icon: 'pi pi-dollar' },
                { label: 'Mais recente', value: formatarData(resumo.mais_recente), icon: 'pi pi-calendar' },
              ]}
            />
            {resumo.lista_truncada && (
              <div style={{ marginTop: '12px' }}>
                <Mensagem texto={`Lista parcial: ${resumo.registros_listados} de ${resumo.total_ocorrencias} registros.`} />
              </div>
            )}
          </>
        )}
      </Secao>

      {categorias.length > 0 && (
        <Secao titulo="Resumo por categoria">
          <Tabela
            linhas={categorias.map(([chave, c]) => ({ chave, ...c }))}
            colunas={[
              { chave: 'categoria', titulo: 'Categoria', render: r => CATEGORIAS[r.chave] ?? rotulo(r.chave) },
              { chave: 'consta', titulo: 'Consta', render: r => (r.consta ? 'Sim' : 'Não') },
              { chave: 'quantidade', titulo: 'Quantidade' },
              { chave: 'mais_recente', titulo: 'Mais recente' },
              { chave: 'valor_total_cents', titulo: 'Valor', render: r => (r.valor_total_cents ? formatarCentavos(r.valor_total_cents) : '—') },
            ]}
          />
        </Secao>
      )}

      {comRegistro.map(([chave, c]) => (
        <Secao
          key={chave}
          titulo={CATEGORIAS[chave] ?? rotulo(chave)}
          extra={<Badge texto={`${c.quantidade} · ${formatarCentavos(c.valor_total_cents)}`} tom="vermelho" />}
        >
          <Tabela linhas={c.registros ?? []} colunas={colunasRegistro} />
        </Secao>
      ))}

      {consultas && (
        <Secao titulo="Consultas ao documento" extra={consultas.resumo && <Badge texto={consultas.resumo} tom="amarelo" />}>
          <Campos
            campos={[
              { label: 'Total', value: consultas.total },
              { label: 'Últimos 30 dias', value: consultas.ultimos_30_dias },
              { label: 'Últimos 90 dias', value: consultas.ultimos_90_dias },
              { label: 'Últimos 180 dias', value: consultas.ultimos_180_dias },
            ]}
          />
          {consultas.itens?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela linhas={consultas.itens} />
            </div>
          )}
          {consultas.historico_mensal?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela linhas={consultas.historico_mensal} />
            </div>
          )}
        </Secao>
      )}

      {socios.length > 0 && (
        <Secao titulo={isPJ ? 'Sócios' : 'Participações societárias'}>
          <Tabela
            linhas={socios}
            colunas={[
              { chave: 'nome', titulo: isPJ ? 'Nome' : 'Empresa' },
              { chave: 'documento', titulo: 'Documento' },
              { chave: 'participacao', titulo: 'Participação' },
              { chave: 'situacao', titulo: 'Situação' },
              { chave: 'uf', titulo: 'UF' },
              { chave: 'tem_restricao', titulo: 'Restrição', render: r => (r.tem_restricao ? <Badge texto="Sim" tom="vermelho" /> : 'Não') },
            ]}
          />
        </Secao>
      )}

      {administradores.length > 0 && (
        <Secao titulo="Administradores">
          <Tabela linhas={administradores} />
        </Secao>
      )}
    </Container>
  );
}
